import { hashCanonicalContent } from '@cogo/schema/canonical-content-hash'

const STORAGE_KEY = 'cogo.studio.chat-generation-draft-attempt.v2'
const SCHEMA_VERSION = 'cogo.studio-chat-generation-draft-attempt.v2' as const
const MAX_ATTEMPTS = 16
const MAX_SCOPE_LENGTH = 160
const ATTEMPT_MAX_AGE_MS = 30 * 60 * 1000
const FUTURE_CLOCK_SKEW_MS = 60 * 1000
const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export interface StoredChatGenerationDraftAttempt {
  schemaVersion: typeof SCHEMA_VERSION
  scopeKey: string
  requestContentHash: string
  idempotencyKey: string
  traceId: string
  startedAt: string
}

interface StoredChatGenerationDraftAttemptSet {
  schemaVersion: typeof SCHEMA_VERSION
  attempts: StoredChatGenerationDraftAttempt[]
}

const memoryAttempts = new Map<string, StoredChatGenerationDraftAttempt>()

export function normalizeChatGenerationRequestContent(value: string): string {
  return value.trim()
}

function normalizedScopeKey(sessionScope?: string): string {
  const scope = sessionScope?.trim()
  return scope && scope.length <= MAX_SCOPE_LENGTH
    ? `session:${scope}`
    : 'anonymous'
}

function attemptKeyFromStored(attempt: StoredChatGenerationDraftAttempt): string {
  return hashCanonicalContent({
    requestContentHash: attempt.requestContentHash,
    scopeKey: attempt.scopeKey,
  })
}

function isFresh(attempt: StoredChatGenerationDraftAttempt): boolean {
  const startedAt = Date.parse(attempt.startedAt)
  return Number.isFinite(startedAt) &&
    startedAt <= Date.now() + FUTURE_CLOCK_SKEW_MS &&
    Date.now() - startedAt <= ATTEMPT_MAX_AGE_MS
}

function isStoredAttempt(value: unknown): value is StoredChatGenerationDraftAttempt {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const record = value as Record<string, unknown>
  if (
    Object.keys(record).length !== 6 ||
    record.schemaVersion !== SCHEMA_VERSION ||
    typeof record.scopeKey !== 'string' ||
    (record.scopeKey !== 'anonymous' &&
      (!record.scopeKey.startsWith('session:') ||
        record.scopeKey.length <= 'session:'.length ||
        record.scopeKey.length > MAX_SCOPE_LENGTH + 'session:'.length)) ||
    typeof record.requestContentHash !== 'string' ||
    !/^sha256:[a-f0-9]{64}$/.test(record.requestContentHash) ||
    typeof record.idempotencyKey !== 'string' ||
    !/^studio-chat:[0-9a-f-]{36}$/i.test(record.idempotencyKey) ||
    !UUID.test(record.idempotencyKey.slice('studio-chat:'.length)) ||
    typeof record.traceId !== 'string' ||
    !UUID.test(record.traceId) ||
    typeof record.startedAt !== 'string'
  ) {
    return false
  }
  return isFresh(record as unknown as StoredChatGenerationDraftAttempt)
}

function readStoredAttempts(): StoredChatGenerationDraftAttempt[] {
  const byKey = new Map(memoryAttempts)
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY) ?? null
    if (!raw) return [...byKey.values()].filter(isFresh)
    const parsed = JSON.parse(raw) as Partial<StoredChatGenerationDraftAttemptSet>
    if (
      parsed.schemaVersion !== SCHEMA_VERSION ||
      !Array.isArray(parsed.attempts) ||
      parsed.attempts.length > MAX_ATTEMPTS
    ) {
      return [...byKey.values()].filter(isFresh)
    }
    for (const attempt of parsed.attempts) {
      if (!isStoredAttempt(attempt)) continue
      byKey.set(attemptKeyFromStored(attempt), attempt)
    }
  } catch {
    // The module-local map is still safe for this tab.
  }
  return [...byKey.values()].filter(isFresh)
}

function persistAttempts(attempts: StoredChatGenerationDraftAttempt[]): void {
  const bounded = attempts
    .filter(isFresh)
    .sort((left, right) => Date.parse(right.startedAt) - Date.parse(left.startedAt))
    .slice(0, MAX_ATTEMPTS)
  memoryAttempts.clear()
  for (const attempt of bounded) {
    memoryAttempts.set(attemptKeyFromStored(attempt), attempt)
  }
  try {
    const payload: StoredChatGenerationDraftAttemptSet = {
      schemaVersion: SCHEMA_VERSION,
      attempts: bounded,
    }
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // The module-local map still preserves retry identity for this tab.
  }
}

export function chatGenerationDraftAttemptForRequestContent(
  requestContent: string,
  sessionScope?: string,
): StoredChatGenerationDraftAttempt {
  const scopeKey = normalizedScopeKey(sessionScope)
  const requestContentHash = hashCanonicalContent({ requestContent })
  const key = hashCanonicalContent({ requestContentHash, scopeKey })
  const attempts = readStoredAttempts()
  const current = attempts.find((attempt) => attemptKeyFromStored(attempt) === key)
  if (current) return current
  const next: StoredChatGenerationDraftAttempt = {
    schemaVersion: SCHEMA_VERSION,
    scopeKey,
    requestContentHash,
    idempotencyKey: `studio-chat:${crypto.randomUUID()}`,
    traceId: crypto.randomUUID(),
    startedAt: new Date().toISOString(),
  }
  persistAttempts([...attempts, next])
  return next
}

export function completeChatGenerationDraftAttempt(
  requestContent: string,
  idempotencyKey: string,
  sessionScope?: string,
): void {
  const requestContentHash = hashCanonicalContent({ requestContent })
  const scopeKey = normalizedScopeKey(sessionScope)
  persistAttempts(
    readStoredAttempts().filter(
      (attempt) =>
        !(
          attempt.scopeKey === scopeKey &&
          attempt.requestContentHash === requestContentHash &&
          attempt.idempotencyKey === idempotencyKey
        ),
    ),
  )
}

export function resetChatGenerationDraftAttemptForTests(): void {
  memoryAttempts.clear()
  try {
    globalThis.localStorage?.removeItem(STORAGE_KEY)
  } catch {
    // no-op
  }
}
